import { ConnectProto } from "@racoon/api";
import { isFinalJobStatus } from "~/shared/lib/job-status";
import { SELECTION_DISABLED_ROUTE_STATUSES } from "./msw-route-list.types";

/** What the trip's "⋯" menu does to its ticked jobs at once, after the undo window. */
export type SelectedJobsAction = "cancel" | "complete";

/** How long the dispatcher has to take a bulk cancel or complete back. */
export const SELECTED_JOBS_UNDO_MS = 5000;

/**
 * The ticked jobs the action may touch. The backend sets the status without
 * checking the current one, so the guard lives here: a settled job (done,
 * cancelled, Невывоз) keeps its record, and a closed trip is left alone. Ids are
 * compared as strings — the RTK cache stores bigints as strings.
 */
export const jobsForSelectedAction = (
  route: ConnectProto.Route,
  selectedIds: ReadonlySet<bigint>,
): ConnectProto.Job[] => {
  if (SELECTION_DISABLED_ROUTE_STATUSES.includes(route.status) || selectedIds.size === 0) return [];
  const selected = new Set(Array.from(selectedIds, String));
  return (route.jobs ?? []).filter(
    (job) => selected.has(String(job.id)) && !isFinalJobStatus(job.status),
  );
};

/**
 * The route sheet's shared check-all, split back per trip: each trip whose jobs can be
 * picked gets the checked ones among its own. Ids compare as strings (the cache's) and are
 * stored as bigints, as each trip's own table stores them.
 */
export const splitCheckedJobs = (
  routes: ConnectProto.Route[],
  checked: ReadonlySet<bigint>,
): [ConnectProto.Route["id"], Set<bigint>][] => {
  const keys = new Set(Array.from(checked, String));
  return routes
    .filter((route) => !SELECTION_DISABLED_ROUTE_STATUSES.includes(route.status))
    .map((route) => [
      route.id,
      new Set(
        (route.jobs ?? []).filter((job) => keys.has(String(job.id))).map((job) => BigInt(job.id)),
      ),
    ]);
};
